import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

// Decode JWT payload without verifying (client-side only, for expiry display)
function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

// Atlassian access tokens expire 1 hour after issue.
// We embed iat (issued-at) in our JWT. Jira token expires at iat + 3600.
function getJiraTokenExpiresAt(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  // iat is the time our JWT was issued, which is also when the Jira token was issued
  const iat = payload.iat;
  if (!iat) return null;
  return (iat + 3600) * 1000; // ms
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jiraTokenExpiresAt, setJiraTokenExpiresAt] = useState(null);
  const [jiraTokenMinutesLeft, setJiraTokenMinutesLeft] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const errorParam = params.get('auth_error');

    if (token) {
      localStorage.setItem('pp_token', token);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (errorParam) {
      console.error('Auth error:', errorParam);
      if (errorParam === 'unauthorized_domain') {
        setAuthError('This tool is only available to Fly Akeed team members.');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    const savedToken = localStorage.getItem('pp_token');
    if (savedToken) {
      const expiresAt = getJiraTokenExpiresAt(savedToken);
      setJiraTokenExpiresAt(expiresAt);

      api.auth.me()
        .then(setUser)
        .catch(() => {
          localStorage.removeItem('pp_token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Tick every 30s — update countdown and auto-refresh at 5 min remaining
  useEffect(() => {
    if (!jiraTokenExpiresAt) return;

    let hasRefreshed = false;

    const tick = async () => {
      const msLeft = jiraTokenExpiresAt - Date.now();
      const minsLeft = Math.max(0, Math.floor(msLeft / 60000));
      setJiraTokenMinutesLeft(minsLeft);

      // Silent auto-refresh at 5 minutes remaining
      if (minsLeft <= 5 && minsLeft > 0 && !hasRefreshed) {
        hasRefreshed = true;
        try {
          console.log('[AUTH] Auto-refreshing Jira token...');
          const result = await api.auth.refresh();
          localStorage.setItem('pp_token', result.token);
          const newExpiresAt = getJiraTokenExpiresAt(result.token);
          setJiraTokenExpiresAt(newExpiresAt);
          hasRefreshed = false;
          console.log('[AUTH] Token refreshed successfully');
        } catch (e) {
          console.warn('[AUTH] Auto-refresh failed:', e.message);
          // Warning banner will show — user can manually re-auth
        }
      }
    };

    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [jiraTokenExpiresAt]);

  const login = () => { window.location.href = api.auth.loginUrl(); };

  const logout = () => {
    localStorage.removeItem('pp_token');
    setUser(null);
    setJiraTokenExpiresAt(null);
    setJiraTokenMinutesLeft(null);
    setAuthError(null);
  };

  // Show warning when <= 10 minutes left on Jira token
  const jiraTokenWarning = user && jiraTokenMinutesLeft !== null && jiraTokenMinutesLeft <= 10;
  const jiraTokenExpired = user && jiraTokenMinutesLeft !== null && jiraTokenMinutesLeft <= 0;

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, logout,
      isFacilitator: !!user,
      jiraTokenMinutesLeft,
      jiraTokenWarning,
      jiraTokenExpired,
      authError
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
